<script setup lang="ts">
//#region import
import { computed, ref, watch } from 'vue';
import { format } from 'date-fns';
import { useI18n } from 'vue-i18n';
import { useLodash } from '@/setup/lodash';
import { useTypedStore } from '@/store';
import { useFormatCurrency } from '@/composables/useFormatCurrency';
import { useFilters } from '@/setup/filters';
import { useApi } from '@/api';
import { type IconControlItem } from '@/types/core';
import type { PostDashboardsStatsData } from '@/types/openapi-ts/types.gen';
import type { DashboardPanelTable, DashboardPanelTypes, DashboardsStatsStat } from '@/types/dashboard';
import type { AvaliableLocales } from '@/i18n';
//#endregion

//#region types
export interface StatValue {
  title: string,
  value: string,
  valueDiff: string,
  valueDiffPercentage: string,
  diffStyle: string,
  titleTooltip?: string;
  description?: string;
}

export interface Props {
  isEdit?: boolean,
  stat: DashboardPanelTable,
  rowIndex?: number,
  index?: number,
  period?: any[],
  isLoading?: boolean,
  propCurrencyCode?: string
}

export interface Emits {
  'removeMetric': [rowIndex: number, type: DashboardPanelTypes, index: number],
  'editMetric': [rowIndex: number, type: DashboardPanelTypes, index: number, data: DashboardPanelTable], 
  'emitDataReport': [rowIndex: number, type: DashboardPanelTypes, index: number, report: DashboardsStatsStat], 
}
//#endregion

//#region define
const props = withDefaults(defineProps<Props>(), {
  isEdit: false,
  period: () => [],
  isLoading: false,
});

const emit = defineEmits<Emits>();
//#endregion

//#region composables
const _ = useLodash();
const store = useTypedStore();
const { t } = useI18n();
const {
  currencyCode,
  formatMetric,
} = useFormatCurrency();
const filters = useFilters();
const api = useApi();
//#endregion

//#region state
const statValue = ref<StatValue>({
  title: '',
  value: '0',
  valueDiff: '',
  valueDiffPercentage: '',
  diffStyle: 'default',
});
const loading = ref(true);
const report = ref<DashboardsStatsStat>({} as DashboardsStatsStat);
//#endregion

//#region computed
const reportsSettings = computed(() => store.getters['reports/reportsSettings']);
const stateDashboardFilter = computed(() => store.state.dashboards.stateDashboardFilter);
const currentArrow = computed(() => {
  const arrowConfig = {
    color: 'var(--danger-color)',
    icon: 'long-arrow-down',
  };
  if (statValue.value.diffStyle === 'positive') {
    arrowConfig.color = 'var(--text-positive)';
    arrowConfig.icon = 'long-arrow-up';
  }
  return arrowConfig;
});

const controlsData = computed<IconControlItem[]>(() => {
  return [
    {
      title: t('dashboards.other.edit'),
      color: 'var(--text-primary)',
      icon: 'pen',
      clickHandler: () => editMetric(props.rowIndex!, 'stat', props.index!, props.stat),
    },
    {
      title: t('dashboards.other.delete'),
      color: 'var(--text-negative)',
      icon: 'trash-alt',
      clickHandler: () => removeMetric(props.rowIndex!, 'stat', props.index!),
    },
  ];
});

const paramsData = computed(() => {
  const period = {
    from: format(props.period[0],'yyyy-MM-dd HH:mm:ss'),
    to: format(props.period[1], 'yyyy-MM-dd HH:mm:ss'),
  };

  const panel = {
    name: props.stat.name,
    type: props.stat.type,
    metric_name: props.stat.metric_name,
    filters: props.stat.filters,

    // current_period и comparison_period не найдены в api, так же не видно их использований
    // current_period: props.stat.current_period,
    // comparison_period: props.stat.comparison_period,
    enable_comparison: props.stat.enable_comparison || false,
  };

  return {
    panel,
    filters: stateDashboardFilter.value,
    ...period,
    currency_code: currencyCode.value,
  } as PostDashboardsStatsData['body'];
});

const filterIsEmpty = computed(() => {
  if (props.stat.filters === null) return true;
  const { search, filters } = props.stat.filters;
  const countKey = Object.keys(props.stat.filters).length;
  const func = (count: number, filter: any) => {
    const keyArray = Object.keys(filter);
    if (count === 1) {
      return keyArray.includes('filters') || keyArray.includes('search');
    }
    if (count === 2) {
      return keyArray.includes('filters') && keyArray.includes('search');
    }
    return false;
  };
  return (
    _.isEmpty(search)
    && _.isEmpty(filters)
    && func(countKey, props.stat.filters)
  );
});
//#endregion

//#region methods
function removeMetric(rowIndex: number, type: DashboardPanelTypes, index: number) {
  emit('removeMetric', rowIndex, type, index);
};

function editMetric(rowIndex: number, type: DashboardPanelTypes, index: number, data: DashboardPanelTable) {
  emit('editMetric', rowIndex, type, index, data);
};

function setValueStat(stat: DashboardPanelTable, value: DashboardsStatsStat) {
  if(value) {
    statValue.value.title = stat.name!;
    statValue.value.titleTooltip = getTranslateMetrics(
      stat.metric_name,
    ).description;
    statValue.value.value = value.current?.formatted;
    statValue.value.valueDiff = value.previous?.formatted || '—';
    if (!value.hasOwnProperty('difference_percent') || value.difference_percent === null) {
      statValue.value.diffStyle = 'default';
      statValue.value.valueDiffPercentage = '';
    } else {
      const valueDiffPercentage = filters.numberAbbreviation(value.difference_percent!);
      statValue.value.valueDiffPercentage = `${valueDiffPercentage || 0}%`;
      statValue.value.diffStyle = value.difference_percent! >= 0
        ? 'positive'
        : 'negative';
    }
    statValue.value.description = getDiscriptionStat();
    statValue.value = _.cloneDeep(statValue.value);
  }
};

function getDiscriptionStat() {
  if (statValue.value.diffStyle === 'default') {
    return 'Comparison Off';
  }
  return t('dashboards.compared.this_vs_last');
};

/**
 * Called outside the component
 */
async function getStat(type?: string) {
  if (type !== 'getLocalData') {
    await getDashboardsStats(paramsData.value);
  } else {
    report.value = props.stat.report!;
  }
  setValueStat(props.stat, report.value);
  loading.value = false;
};

function getTranslateMetrics(metric: string) {
  return formatMetric(reportsSettings.value.metrics.find((e) => e.column_name === metric));
};

async function getDashboardsStats(params: PostDashboardsStatsData['body']) {
  const {
    data: { payload },
  } = await api.dashboardsStats(params);
  report.value = payload as DashboardsStatsStat;
  emit(
    'emitDataReport',
    props.rowIndex!,
    'stat',
    props.index!,
    report.value,
  );
};
//#endregion

//#region watch
watch(() => props.propCurrencyCode, (newVal) => {
  currencyCode.value = newVal;
}, {immediate: true});

watch(currencyCode, () => {
  getStat();
});

watch(() => props.stat, () => {
  getStat('getLocalData');
});
//#endregion

defineExpose({
  loading,
  getStat,
});
</script>

<template>
  <div
    :class="[
      'dashboard-metric-stat',
      {'dashboard-metric-stat_loading': loading || isLoading}
    ]"
  >
    <div class="dashboard-metric-stat__title">
      <UiTitle
        class="t:font-medium"
        :filtered="!filterIsEmpty"
        :tooltip="statValue.titleTooltip"
      >
        {{ statValue.title }}
      </UiTitle>
      <div
        v-if="isEdit"
        class="t:flex t:space-x-12 t:ml-8"
      >
        <ActionIcon
          v-for="control in controlsData"
          :key="control.title"
          :icon="control.icon"
          :color="control.color"
          :tooltip="control.title"
          :is-overlay="false"
          @click="control.clickHandler"
        />
      </div>
    </div>
    <div
      :class="[
        'dashboard-metric-stat__current',
        `dashboard-metric-stat__current_${statValue.diffStyle}`
      ]"
    >
      <div class="dashboard-metric-stat__current-value">
        {{ statValue.value === '' ? '—' : statValue.value }}
      </div>
      <div class="dashboard-metric-stat__current-percent">
        <UiIcon
          v-if="statValue.valueDiffPercentage !== ''"
          :size="16"
          :name="currentArrow.icon"
          :color="currentArrow.color"
          class="t:mr-4"
        />
        {{ statValue.valueDiffPercentage === '' ? '—' : statValue.valueDiffPercentage }}
      </div>
    </div>
    <div class="dashboard-metric-stat__prev">
      {{ statValue.valueDiff === '' ? '—' : statValue.valueDiff }}
    </div>
  </div>
</template>

<style lang="scss">
.dashboard-metric-stat{
  width: 100%;
  padding: 16px;
  border-radius: 8px;
  background: var(--background-surface);

  &__title {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 8px;
    min-height: 16px;
    position: relative;
  }

  &__current {
    position: relative;
    display: flex;
    align-items: center;
    &_positive {
      color: var(--text-positive);
    }
    &_negative {
      color: var(--text-negative);
    }
  }
  &__current-value {
    font-weight: 500;
    margin-right: 8px;
    font-size: 20px;
    line-height: normal;
  }
  &__current-percent{
    border-radius: 1px;
    border-left: 1px solid var(--border-default);
    padding-left: 8px;
    font-weight: 500;
    line-height: 1;
    display: flex;
  }

  &__prev {
    margin: 4px 0;
    display: flex;
    align-items: center;
    position: relative;
    font-weight: 500;
    color: var(--text-body);
    font-size: 14px;
    line-height: normal;
  }

  &_loading &{
    &__title,
    &__current,
    &__prev {
      &:before {
        content: '';
        z-index: 2;
        position: absolute;
        width: 100%;
        height: 100%;
        background-color: #dbe0e8;
        border-radius: 4px;
      }
    }

    &__current-percent {
      display: none;
    }
  }
}
</style>
